// src/components/admin/DocumentViewer.tsx

import React from 'react';
import { FileText, Eye, Image, FileArchive } from 'lucide-react';
import { MEDIA_BASE_URL } from '../../api/auth';

interface DocumentViewerProps {
  file: string;
  label: string;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ file, label }) => {
  if (!file) return null;

  // ✅ Ensure file path has /media/ prefix
  const getFullPath = (path: string) => {
    if (path.startsWith('http')) return path;
    if (path.startsWith('/media/')) return path;

    return `/media/${path}`;
  };

  const mediaUrl = `${MEDIA_BASE_URL}${getFullPath(file)}`;

  const filename = file.split('/').pop() || 'Document';

  const getFileIcon = () => {
    const ext = filename.split('.').pop()?.toLowerCase();
    const iconClass = "h-5 w-5 flex-shrink-0";
    
    switch (ext) {
      case 'pdf':
        return <FileText className={`${iconClass} text-red-500`} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
        return <Image className={`${iconClass} text-blue-500`} />;
      case 'doc':
      case 'docx':
        return <FileText className={`${iconClass} text-blue-600`} />;
      case 'xls':
      case 'xlsx':``
        return <FileText className={`${iconClass} text-green-600`} />;
      case 'zip':
      case 'rar':
        return <FileArchive className={`${iconClass} text-yellow-500`} />;
      default:
        return <FileText className={`${iconClass} text-gray-400`} />;
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
      {getFileIcon()}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-700">{label}</p>
        <p className="text-xs text-gray-500 truncate">{filename}</p>
      </div>
      <a
        href={mediaUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors flex items-center gap-1 flex-shrink-0"
      >
        <Eye className="h-3.5 w-3.5" />
        View
      </a>
    </div>
  );
};

export default DocumentViewer;